import { clsx } from 'clsx'
import { DragEvent, ReactNode } from 'react'
import { DragHandleIcon } from './icons'
import { Tip } from './Tip'

export const Checklist = {
  /**
   * The main "table" container. We define one grid row for the "header"
   * and wrap the children (the body rows) in another <div>.
   */
  Table({ children }: { children: ReactNode }) {
    return (
      <div className="w-full overflow-hidden font-sans">
        {/* Header row */}
        <div className="grid grid-cols-[2rem_2rem_13rem_minmax(0,1fr)_9rem] border-b border-b-gray-300">
          {/* Each "header cell" is just a <div> now */}
          <div className="py-3.5 pl-4 text-left text-xs font-thin uppercase text-gray-500" />
          <div className="py-3.5 pl-4 text-left text-xs font-thin uppercase text-gray-500" />
          <div className="py-3.5 px-2 text-left text-xs font-thin uppercase text-gray-500">
            Name
          </div>
          <div className="py-3.5 text-left text-xs font-thin uppercase text-gray-500">Note</div>
          <div className="py-3.5 pl-4 pr-3 text-left text-xs font-thin uppercase text-gray-500" />
        </div>

        {/* Body rows go here */}
        <div className="divide-y divide-gray-200">{children}</div>
      </div>
    )
  },

  /**
   * Each "Row" is now a <div> with the exact same grid definition as the header,
   * so columns will align. We just replace <tr> with <div> and each cell is <div>.
   */
  Row({
    children,
    checked,
    draggable,
    onDragStart,
    onDragOver,
    onDrop,
    onDragLeave,
    dragStatus,
    ariaLabel
  }: {
    children: ReactNode
    checked?: boolean
    draggable?: boolean
    onDragStart?: (event: DragEvent<HTMLElement>) => void
    onDragOver?: (event: DragEvent<HTMLElement>) => void
    onDrop?: (event: DragEvent<HTMLElement>) => void
    onDragLeave?: (event: DragEvent<HTMLElement>) => void
    dragStatus?: 'before' | 'after' | 'dragging'
    ariaLabel?: string
  }) {
    return (
      <div
        className={clsx(
          'group/checklist-row grid min-h-11 items-center grid-cols-[2rem_2rem_13rem_minmax(0,1fr)_9rem]',
          {
            'hover:bg-stone-100': !checked,
            'bg-blue-100 hover:bg-blue-200': checked,
            'shadow-[inset_0_2px_0_0_#354dcc]': dragStatus === 'before',
            'shadow-[inset_0_-2px_0_0_#354dcc]': dragStatus === 'after',

            'opacity-80': dragStatus === 'dragging'
          }
        )}
        draggable={draggable}
        aria-label={ariaLabel}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragLeave={onDragLeave}
      >
        {children}
      </div>
    )
  },

  /**
   * The "drag handle" cell. Now just a <div> that takes up a column in the grid.
   */
  DragHandleCell({ enabled = true }: { enabled?: boolean }) {
    return (
      <div className="relative flex items-center whitespace-nowrap px-2 py-2 text-sm text-gray-500">
        {enabled && (
          <DragHandleIcon className="invisible cursor-move text-[#545666] group-hover/checklist-row:visible" />
        )}
      </div>
    )
  },

  /**
   * Checkbox cell. Replaces <td> with a <div>.
   */
  CheckboxCell(props: {
    checked: boolean
    disabled?: boolean
    onChange: (newValue: boolean) => void
  }) {
    return (
      <div className="relative flex items-center justify-center whitespace-nowrap px-2 py-2 text-sm text-gray-500">
        <input
          type="checkbox"
          className="border-gray-300 px-2"
          disabled={props.disabled}
          checked={props.checked}
          onChange={(e) => props.onChange(e.target.checked)}
        />
      </div>
    )
  },

  /**
   * MainCell, used for the primary text content. Just a <div> in the row grid.
   */
  MainCell({ children, crossedOut }: { children: ReactNode; crossedOut?: boolean }) {
    return (
      <div
        className={clsx(
          'flex flex-row items-center gap-2 whitespace-nowrap px-2 py-2 text-base font-semibold text-gray-900',
          {
            'line-through': crossedOut
          }
        )}
      >
        {children}
      </div>
    )
  },

  /**
   * Link cell. Another <div>.
   */
  LinkCell({ children }: { children?: ReactNode }) {
    return (
      <div className="flex flex-row items-center gap-2 whitespace-nowrap px-2 py-2 text-sm text-gray-500">
        {children}
      </div>
    )
  },

  /**
   * Shared spacing for row actions.
   */
  ActionsCell({ children }: { children: ReactNode }) {
    return (
      <div className="flex items-center justify-end gap-2 whitespace-nowrap px-3 py-2 text-sm text-gray-500">
        {children}
      </div>
    )
  },

  /**
   * Button with a tooltip, same as before—just not a table cell now.
   */
  Button({
    children,
    tip,
    ariaLabel,
    onClick,
    disabled
  }: {
    children?: ReactNode
    tip?: string
    ariaLabel?: string
    onClick: () => void
    disabled?: boolean
  }) {
    return (
      <Tip title={tip}>
        <button
          className="relative h-6 max-h-[40px] w-6 max-w-[40px] shrink-0 select-none rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          onClick={onClick}
        >
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
            {children}
          </span>
        </button>
      </Tip>
    )
  }
}
